/**
 * Pillar 6 — purpose-built clarification thread.
 *
 * This is NOT a chat. It is the operational clarification trail tied to
 * one inbox item / one revision. Foreman and superintendent each post
 * once-or-twice; super can mark resolved to close the question.
 */
import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


export default function QuestionThread({ itemId, questions, onPosted }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const post = async (resolve = false) => {
    if (!body.trim() && !resolve) return;
    setSending(true);
    try {
      await api.post(`/foreman/inbox/${itemId}/questions`, {
        body: body.trim(),
        resolve,
      });
      setBody("");
      onPosted?.();
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't post.");
    } finally {
      setSending(false);
    }
  };

  if ((questions || []).length === 0 && !sending && !body) {
    return null;
  }

  return (
    <div className="mt-2 mb-4 border-t border-[var(--fop-border)] pt-3" data-testid="inbox-question-thread">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2">
        Clarification — between foreman & super
      </div>
      <ol className="space-y-2 mb-2">
        {(questions || []).map((q) => (
          <li
            key={q.id}
            data-testid={`inbox-question-${q.id}`}
            className={`p-2.5 rounded border ${
              q.side === "foreman"
                ? "border-[var(--fop-warning)]/40 bg-[var(--fop-warning-soft, rgba(245,158,11,0.08))]"
                : "border-[var(--fop-primary)]/40 bg-[var(--fop-primary-soft)]"
            }`}
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-0.5 flex items-center gap-2">
              <span>{q.side === "foreman" ? "Foreman" : "Super"} · {q.author_name}</span>
              <span>· {new Date(q.created_at).toLocaleString()}</span>
              {q.resolved && (
                <span className="inline-flex items-center gap-0.5 text-[var(--fop-success)]">
                  <CheckCircle2 size={11} /> resolved
                </span>
              )}
            </div>
            <div className="text-[13px] text-[var(--fop-text)] whitespace-pre-wrap">{q.body}</div>
          </li>
        ))}
      </ol>
      <div className="flex flex-col gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Reply…"
          className="w-full px-2.5 py-2 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text)] text-sm"
          rows={2}
          maxLength={1500}
          data-testid="inbox-question-input"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => post(false)}
            disabled={!body.trim() || sending}
            className="btn btn-primary text-[12px] inline-flex items-center gap-1"
            data-testid="inbox-question-send"
          >
            <Send size={12} /> Send reply
          </button>
          <button
            type="button"
            onClick={() => post(true)}
            disabled={sending}
            className="text-[12px] text-[var(--fop-success)] inline-flex items-center gap-1 hover:underline"
            data-testid="inbox-question-resolve"
            title="Close the clarification — super-side only"
          >
            <CheckCircle2 size={12} /> Mark resolved
          </button>
        </div>
      </div>
    </div>
  );
}
